import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  TableSortLabel,
  CircularProgress,
  Alert,
  Snackbar,
  TablePagination,
  Chip,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import api from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.js';
import { formatarTelefone } from '../../utils/formatadores.js';
import Preloader from '../../components/Preloader';

const PERFIL_COLORS = {
  superusuario: 'secondary',
  gerente: 'error',
  administrador: 'warning',
  operador: 'info',
  beneficiario: 'default'
};

const PERFIL_LABELS = {
  superusuario: 'Superusuário',
  gerente: 'Gerente',
  administrador: 'Administrador',
  operador: 'Operador',
  beneficiario: 'Beneficiário'
};

export default function ListagemUsuarios() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [usuarioVisualizar, setUsuarioVisualizar] = useState(null);
  const [usuarioParaExcluir, setUsuarioParaExcluir] = useState(null);

  const [filtros, setFiltros] = useState({
    st_nome: "",
    st_email: "",
    en_perfil: "",
  });

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("st_nome");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    carregarUsuarios();
  }, []);

  const carregarUsuarios = async () => {
    setLoading(true);
    try {
      const response = await api.get('/usuario');
      setUsuarios(response.data.dados || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (event) => {
    const { name, value } = event.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(0); // Resetar página ao filtrar
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const visualizarUsuario = (usuario) => {
    setUsuarioVisualizar(usuario);
    setOpenView(true);
  };

  const fecharVisualizacao = () => {
    setOpenView(false);
    setUsuarioVisualizar(null);
  };

  const confirmarExclusao = (usuario) => {
    if (!usuario) {
      setError("Usuário não encontrado");
      return;
    }
    setUsuarioParaExcluir(usuario);
    setOpenDeleteConfirm(true);
  };

  const fecharConfirmacaoExclusao = () => {
    setOpenDeleteConfirm(false);
    setUsuarioParaExcluir(null);
  };

  const deletarUsuario = async () => {
    if (!usuarioParaExcluir) {
      setError("Nenhum usuário selecionado para exclusão");
      return;
    }

    try {
      setLoading(true);
      const usuarioId = usuarioParaExcluir.id_usuario || usuarioParaExcluir.id;

      if (!usuarioId) {
        setError("ID do usuário não encontrado");
        fecharConfirmacaoExclusao();
        return;
      }

      await api.delete(`/usuario/${usuarioId}`);

      setSuccess("Usuário excluído com sucesso!");
      fecharConfirmacaoExclusao();
      setTimeout(() => {
        carregarUsuarios();
      }, 500);
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);

      if (error.response?.status === 401) {
        setError("Sessão expirada. Por favor, faça login novamente.");
      } else if (error.response?.status === 403) {
        setError("Você não tem permissão para excluir usuários.");
      } else if (error.response?.status === 404) {
        setError("Usuário não encontrado.");
      } else {
        const mensagemErro = error.response?.data?.mensagem ||
          error.response?.data?.error ||
          error.message ||
          "Erro ao excluir usuário. Tente novamente.";
        setError(mensagemErro);
      }
      fecharConfirmacaoExclusao();
    } finally {
      setLoading(false);
    }
  };

  // Filtrar e ordenar usuários
  const usuariosFiltrados = usuarios
    .filter((usuario) => {
      const nomeMatch = !filtros.st_nome ||
        usuario.st_nome?.toLowerCase().includes(filtros.st_nome.toLowerCase());
      const emailMatch = !filtros.st_email ||
        usuario.st_email?.toLowerCase().includes(filtros.st_email.toLowerCase());
      const perfilMatch = !filtros.en_perfil ||
        usuario.en_perfil === filtros.en_perfil;

      return nomeMatch && emailMatch && perfilMatch;
    })
    .sort((a, b) => {
      let aValue = a[orderBy] || '';
      let bValue = b[orderBy] || '';

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (order === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

  const dataHoraAtual = new Date().toLocaleString('pt-BR');

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, height: '100vh', overflow: 'auto' }}>
      {/* Snackbars para notificações */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Box interno com margens de 1,5% */}
      <Box sx={{
        margin: '0 1.5% 1.5% 1.5%',
        height: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: { xs: 2, sm: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <PeopleIcon sx={{
              fontSize: { xs: '1.5rem', sm: '2rem' },
              color: '#20ac9c'
            }} />
            Usuários
          </Typography>
          {(user?.perfil === 'superusuario' || user?.perfil === 'gerente' || user?.perfil === 'administrador') && (
            <Button
              variant="contained"
              onClick={() => navigate("/usuarios/cadastro")}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 'auto', sm: '200px' },
                backgroundColor: '#20ac9c',
                '&:hover': {
                  backgroundColor: '#167a6f'
                }
              }}
            >
              <AddIcon /> Novo Usuário
            </Button>
          )}
        </Box>

        {/* Container da Tabela */}
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'auto',
            height: {
              xs: 'calc(90vh - 140px)',
              sm: 'calc(90vh - 120px)',
              md: 'calc(90vh - 120px)',
              lg: 'calc(90vh - 120px)'
            },
            maxHeight: {
              xs: 'calc(90vh - 140px)',
              sm: 'calc(90vh - 120px)',
              md: 'calc(90vh - 120px)',
              lg: 'calc(90vh - 120px)'
            },
            width: '100%',
            minWidth: { xs: '100%', sm: '600px' },
            maxWidth: '100%',
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 2px 8px rgba(0, 0, 0, 0.15)'
              : '0 2px 8px rgba(0, 0, 0, 0.06)',
            borderRadius: 2,
            border: 'none'
          }}
        >
          <Table sx={{ minWidth: '100%', tableLayout: 'fixed' }}>
            <TableHead sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: (theme) => theme.palette.background.paper,
              '& .MuiTableCell-root': {
                backgroundColor: 'inherit',
                borderBottom: '1px solid rgba(224, 224, 224, 1)'
              }
            }}>
              <TableRow>
                <TableCell sx={{
                  width: { xs: '25%', sm: '20%' },
                  minWidth: { xs: '150px', sm: '120px' }
                }}>
                  <TableSortLabel
                    active={orderBy === "st_nome"}
                    direction={orderBy === "st_nome" ? order : "asc"}
                    onClick={() => handleRequestSort("st_nome")}
                  >
                    Nome
                  </TableSortLabel>
                  <TextField
                    name="st_nome"
                    size="small"
                    fullWidth
                    value={filtros.st_nome}
                    onChange={handleFiltroChange}
                    placeholder="Filtrar por nome"
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }
                    }}
                  />
                </TableCell>
                <TableCell sx={{
                  width: { xs: '25%', sm: '20%' },
                  minWidth: { xs: '120px', sm: '100px' }
                }}>
                  <TableSortLabel
                    active={orderBy === "st_email"}
                    direction={orderBy === "st_email" ? order : "asc"}
                    onClick={() => handleRequestSort("st_email")}
                  >
                    Email
                  </TableSortLabel>
                  <TextField
                    name="st_email"
                    size="small"
                    fullWidth
                    value={filtros.st_email}
                    onChange={handleFiltroChange}
                    placeholder="Filtrar por email"
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }
                    }}
                  />
                </TableCell>
                <TableCell sx={{
                  width: { xs: '15%', sm: '12%' },
                  minWidth: { xs: '100px', sm: '80px' }
                }}>
                  <TableSortLabel
                    active={orderBy === "st_telefone"}
                    direction={orderBy === "st_telefone" ? order : "asc"}
                    onClick={() => handleRequestSort("st_telefone")}
                  >
                    Telefone
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{
                  width: { xs: '15%', sm: '12%' },
                  minWidth: { xs: '100px', sm: '80px' }
                }}>
                  <TableSortLabel
                    active={orderBy === "en_perfil"}
                    direction={orderBy === "en_perfil" ? order : "asc"}
                    onClick={() => handleRequestSort("en_perfil")}
                  >
                    Perfil
                  </TableSortLabel>
                  <TextField
                    name="en_perfil"
                    select
                    size="small"
                    fullWidth
                    value={filtros.en_perfil}
                    onChange={handleFiltroChange}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="superusuario">Superusuário</MenuItem>
                    <MenuItem value="gerente">Gerente</MenuItem>
                    <MenuItem value="administrador">Administrador</MenuItem>
                    <MenuItem value="operador">Operador</MenuItem>
                    <MenuItem value="beneficiario">Beneficiário</MenuItem>
                  </TextField>
                </TableCell>
                <TableCell sx={{
                  width: { xs: '10%', sm: '8%' },
                  minWidth: { xs: '80px', sm: '60px' },
                  textAlign: 'center'
                }}>
                  Status
                </TableCell>
                <TableCell sx={{
                  width: { xs: '15%', sm: '12%' },
                  minWidth: { xs: '100px', sm: '80px' },
                  textAlign: 'center'
                }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Preloader message="Carregando usuários..." />
                  </TableCell>
                </TableRow>
              ) : usuariosFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhum usuário encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                usuariosFiltrados
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((usuario, index) => (
                    <TableRow
                      key={usuario.id_usuario || usuario.id || index}
                      sx={{
                        backgroundColor: (theme) =>
                          index % 2 === 0
                            ? theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.02)'
                              : 'rgba(0, 0, 0, 0.02)'
                            : 'transparent',
                        '&:hover': {
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.08)'
                              : 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <TableCell
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          height: '40px',
                          padding: '8px 16px',
                          verticalAlign: 'middle'
                        }}
                      >
                        {usuario.st_nome}
                      </TableCell>
                      <TableCell
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          height: '40px',
                          padding: '8px 16px',
                          verticalAlign: 'middle'
                        }}
                      >
                        {usuario.st_email}
                      </TableCell>
                      <TableCell
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          height: '40px',
                          padding: '8px 16px',
                          verticalAlign: 'middle'
                        }}
                      >
                        {usuario.st_telefone ? formatarTelefone(usuario.st_telefone) : '-'}
                      </TableCell>
                      <TableCell
                        sx={{
                          height: '40px',
                          padding: '8px 16px',
                          verticalAlign: 'middle'
                        }}
                      >
                        <Chip
                          label={PERFIL_LABELS[usuario.en_perfil] || usuario.en_perfil}
                          color={PERFIL_COLORS[usuario.en_perfil] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          height: '40px',
                          padding: '8px 16px',
                          verticalAlign: 'middle',
                          textAlign: 'center'
                        }}
                      >
                        <Chip
                          label={usuario.bo_ativo ? "Ativo" : "Inativo"}
                          color={usuario.bo_ativo ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          height: '40px',
                          padding: '8px 16px',
                          verticalAlign: 'middle',
                          textAlign: 'center'
                        }}
                      >
                        <Tooltip title="Visualizar">
                          <IconButton size="small" onClick={() => visualizarUsuario(usuario)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {(user?.perfil === 'superusuario' || user?.perfil === 'gerente' || user?.perfil === 'administrador') && (
                          <>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/usuarios/editar/${usuario.id_usuario || usuario.id}`)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(user?.perfil === 'superusuario' || user?.perfil === 'gerente') && (
                              <Tooltip title="Excluir">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    confirmarExclusao(usuario);
                                  }}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação */}
        <TablePagination
          component="div"
          count={usuariosFiltrados.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Itens por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            borderTop: 'none',
            width: '100%',
            marginTop: 0
          }}
        />
      </Box>

      {/* Dialogs */}
      {/* Dialog de Visualização */}
      <Dialog
        open={openView}
        onClose={fecharVisualizacao}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderBottom: 1,
          borderColor: 'divider',
          padding: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          Visualizar Usuário
          <IconButton onClick={fecharVisualizacao}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Nome Completo"
              value={usuarioVisualizar?.st_nome || ""}
              disabled
            />
            <TextField
              fullWidth
              label="Email"
              value={usuarioVisualizar?.st_email || ""}
              disabled
            />
            <TextField
              fullWidth
              label="Telefone"
              value={usuarioVisualizar?.st_telefone ? formatarTelefone(usuarioVisualizar.st_telefone) : ""}
              disabled
            />
            <TextField
              fullWidth
              label="Perfil"
              value={PERFIL_LABELS[usuarioVisualizar?.en_perfil] || usuarioVisualizar?.en_perfil || ""}
              disabled
            />
            <TextField
              fullWidth
              label="Status"
              value={usuarioVisualizar?.bo_ativo ? "Ativo" : "Inativo"}
              disabled
            />
            {usuarioVisualizar?.Empresa && (
              <TextField
                fullWidth
                label="Empresa"
                value={usuarioVisualizar.Empresa.st_nome || ""}
                disabled
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderTop: 1,
          borderColor: 'divider',
          padding: 2
        }}>
          <Button onClick={fecharVisualizacao} variant="contained">
            FECHAR
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={openDeleteConfirm} onClose={fecharConfirmacaoExclusao} maxWidth="xs" fullWidth>
        <DialogTitle sx={{
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderBottom: 1,
          borderColor: 'divider',
          padding: 2
        }}>
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent sx={{ padding: 2 }}>
          <Typography>
            Tem certeza que deseja excluir o usuário "{usuarioParaExcluir?.st_nome}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderTop: 1,
          borderColor: 'divider',
          padding: 2
        }}>
          <Button onClick={fecharConfirmacaoExclusao}>CANCELAR</Button>
          <Button
            variant="contained"
            color="error"
            onClick={deletarUsuario}
            disabled={loading}
          >
            EXCLUIR
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
